'use client';
import { useState, useEffect, useRef } from 'react';
import Logo from './Logo';
import HeaderMenuItem from './HeaderMenuItem';
import HeaderSubMenuItem from './HeaderSubMenuItem';
import Link from '../ui/Link';

interface SearchProduct {
    id: number;
    name: string;
    category: string;
    price: string;
    image: string;
    href: string;
}

const sampleProducts: SearchProduct[] = [
    { id: 1, name: 'Yves Saint Classic Shirt', category: 'Men', price: '₹39.99', image: '/images/img_image.png', href: '/shop' },
    { id: 2, name: 'Yves Saint Premium Sunglasses', category: 'Bags & Accessories', price: '₹49.99', image: '/images/img_image_382x336.png', href: '/shop' },
    { id: 3, name: 'Yves Saint Summer Dress', category: 'Women', price: '₹34.99', image: '/images/img_image_4.png', href: '/shop' },
    { id: 4, name: 'Yves Saint Casual Shoulder Bag', category: 'Bags', price: '₹29.99', image: '/images/img_image_5.png', href: '/shop' },
    { id: 5, name: 'Yves Saint Luxe Top', category: 'Women', price: '₹69.99', image: '/images/img_image_6.png', href: '/shop' },
    { id: 6, name: 'Yves Saint Kids Apparel Stack', category: 'Kids', price: '₹24.99', image: '/images/img_image_1.png', href: '/shop' },
    { id: 7, name: 'Yves Saint Sport Wear & Shoes', category: 'Shoes', price: '₹59.99', image: '/images/img_image_2.png', href: '/shop' },
    { id: 8, name: 'Yves Saint Casual Jacket', category: 'Men', price: '₹89.99', image: '/images/img_image_3.png', href: '/shop' },
];

const Header = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (searchOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
            const handleKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'Escape') setSearchOpen(false);
            };
            window.addEventListener('keydown', handleKeyDown);
            return () => window.removeEventListener('keydown', handleKeyDown);
        }
    }, [searchOpen]);

    const filteredProducts = searchQuery.trim() === ''
        ? []
        : sampleProducts.filter(p =>
            p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.category.toLowerCase().includes(searchQuery.toLowerCase())
        );

    return (
        <header className="w-full relative z-40">
            {/* Top Banner with Right-to-Left Marquee */}
            <div className="w-full bg-primary-brand-dark relative z-50">
                <div className="w-full max-w-[1920px] mx-auto pl-0 pr-4 sm:pr-6 lg:pr-[60px]">
                    <div className="flex flex-row justify-between items-center py-1.5 sm:py-2 gap-4">
                        {/* Marquee Area (Extends 100% to screen left edge, stops before Help & Support on right) */}
                        <div className="marquee-container flex-1 overflow-hidden relative min-w-0 pr-3 sm:pr-6 flex pl-2 sm:pl-4">
                            {/* Track 1 */}
                            <div className="flex shrink-0 gap-10 sm:gap-14 animate-marquee-track pr-10 sm:pr-14">
                                <span className="text-[11px] sm:text-base text-text-on-dark font-semibold leading-tight sm:leading-base whitespace-nowrap">
                                    Summer Special Up to 50% OFF on Selected Items! Shop Now
                                </span>
                                <span className="text-[11px] sm:text-base text-text-on-dark font-semibold leading-tight sm:leading-base whitespace-nowrap">
                                    Summer Special Up to 50% OFF on Selected Items! Shop Now
                                </span>
                                <span className="text-[11px] sm:text-base text-text-on-dark font-semibold leading-tight sm:leading-base whitespace-nowrap">
                                    Summer Special Up to 50% OFF on Selected Items! Shop Now
                                </span>
                            </div>
                            {/* Track 2 (Identical duplicate for 100% seamless infinite loop) */}
                            <div className="flex shrink-0 gap-10 sm:gap-14 animate-marquee-track pr-10 sm:pr-14" aria-hidden="true">
                                <span className="text-[11px] sm:text-base text-text-on-dark font-semibold leading-tight sm:leading-base whitespace-nowrap">
                                    Summer Special Up to 50% OFF on Selected Items! Shop Now
                                </span>
                                <span className="text-[11px] sm:text-base text-text-on-dark font-semibold leading-tight sm:leading-base whitespace-nowrap">
                                    Summer Special Up to 50% OFF on Selected Items! Shop Now
                                </span>
                                <span className="text-[11px] sm:text-base text-text-on-dark font-semibold leading-tight sm:leading-base whitespace-nowrap">
                                    Summer Special Up to 50% OFF on Selected Items! Shop Now
                                </span>
                            </div>
                        </div>

                        {/* Help & Support + Links (Right Anchored) */}
                        <div className="flex flex-row gap-3 sm:gap-[30px] lg:gap-[40px] items-center text-xs sm:text-sm flex-shrink-0">
                            <HeaderMenuItem href="/support" text="Help & Support" variant="light" />
                            <HeaderMenuItem href="/track-order" text="Track Order" variant="light" />
                            <HeaderSubMenuItem text="EN / USD" variant="light" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Header */}
            <div className="w-full border-b border-border-light bg-[#FAF6F2] sticky top-0 z-30">
                <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px]">
                    <div className="flex flex-row justify-between items-center py-2.5 sm:py-3.5 lg:py-4 gap-4">
                        {/* Logo */}
                        <Logo
                            src="/images/img_header_logo.png"
                            alt="NEHDO Wellness"
                            width={202}
                            height={70}
                            className="w-[110px] sm:w-[145px] lg:w-[170px]"
                        />

                        {/* Desktop Navigation */}
                        <nav className="hidden lg:flex flex-row items-center gap-[40px]">
                            <HeaderMenuItem href="/" text="Home" />
                            <HeaderMenuItem href="/shop" text="Shop" />
                            <HeaderMenuItem href="/categories" text="Categories" />
                            <HeaderMenuItem href="/new-arrivals" text="New Arrivals" />
                            <HeaderMenuItem href="/about" text="About" />
                            <HeaderMenuItem href="/contact" text="Contact" />
                        </nav>

                        {/* Right Desktop Action Icons */}
                        <div className="hidden lg:flex flex-row items-center gap-4 sm:gap-5 lg:gap-[15px]">
                            <button
                                onClick={() => setSearchOpen(true)}
                                aria-label="Open Search"
                                className="p-1 hover:opacity-80 transition-opacity cursor-pointer"
                            >
                                <img src="/images/img_frame.svg" alt="Search" className="w-6 h-6 lg:w-[30px] lg:h-[30px]" />
                            </button>
                            <Link href="/wishlist" ariaLabel="Wishlist">
                                <img src="/images/img_vector.svg" alt="Wishlist" className="w-5 h-5 lg:w-[24px] lg:h-[24px]" />
                            </Link>
                            <Link href="/account" ariaLabel="Account">
                                <img src="/images/img_frame_black_900.svg" alt="Account" className="w-6 h-6 lg:w-[30px] lg:h-[30px]" />
                            </Link>
                            <Link href="/cart" ariaLabel="Shopping Cart">
                                <img src="/images/img_frame_black_900_30x30.svg" alt="Cart" className="w-6 h-6 lg:w-[30px] lg:h-[30px]" />
                            </Link>
                        </div>

                        {/* Hamburger Button (Mobile Only) */}
                        <button
                            className="block lg:hidden p-2 text-text-primary hover:text-primary-brand focus:outline-none cursor-pointer"
                            onClick={() => setMenuOpen(true)}
                            aria-label="Open menu"
                        >
                            <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* In-Place Live Search Overlay Modal */}
            {searchOpen && (
                <div className="fixed inset-0 z-[200] flex flex-col justify-start">
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
                        onClick={() => setSearchOpen(false)}
                    />

                    {/* Search Panel Header */}
                    <div className="relative z-[201] w-full bg-white border-b border-border-light shadow-xl py-6 px-4 sm:px-6 lg:px-[60px]">
                        <div className="max-w-[1920px] mx-auto flex flex-col gap-4">
                            <div className="flex items-center gap-4">
                                <svg className="w-6 h-6 text-primary-brand flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>

                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search products, styles, categories..."
                                    className="w-full text-lg sm:text-2xl text-text-primary placeholder:text-text-opacity-60 bg-transparent outline-none font-medium"
                                />

                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="text-xs font-semibold uppercase text-text-opacity-60 hover:text-text-primary px-2 py-1"
                                    >
                                        Clear
                                    </button>
                                )}

                                <button
                                    onClick={() => setSearchOpen(false)}
                                    className="w-10 h-10 rounded-full bg-background-tertiary flex items-center justify-center text-text-primary hover:bg-primary-brand hover:text-white transition-colors cursor-pointer flex-shrink-0"
                                    aria-label="Close search"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Popular Searches Quick Tags */}
                            <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border-light/60">
                                <span className="text-xs font-bold uppercase text-text-opacity-60 mr-1">Trending:</span>
                                {['Men', 'Women', 'Sunglasses', 'Bags', 'Shoes', 'Summer'].map(tag => (
                                    <button
                                        key={tag}
                                        onClick={() => setSearchQuery(tag)}
                                        className="px-3 py-1 rounded-full bg-background-tertiary hover:bg-primary-brand hover:text-white text-xs font-semibold text-text-primary transition-colors cursor-pointer"
                                    >
                                        {tag}
                                    </button>
                                ))}
                            </div>

                            {/* Instant Live Search Results */}
                            {searchQuery.trim() !== '' && (
                                <div className="mt-4 max-h-[60vh] overflow-y-auto border-t border-border-light pt-4">
                                    <p className="text-xs font-bold text-text-opacity-60 uppercase mb-3">
                                        Matching Results ({filteredProducts.length})
                                    </p>
                                    {filteredProducts.length > 0 ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                            {filteredProducts.map(product => (
                                                <Link
                                                    key={product.id}
                                                    href={product.href}
                                                    onClick={() => setSearchOpen(false)}
                                                    className="flex items-center gap-3 p-3 rounded-xl bg-background-tertiary hover:bg-background-secondary transition-colors group"
                                                >
                                                    <div className="w-14 h-14 rounded-lg bg-white overflow-hidden flex-shrink-0 p-1">
                                                        <img src={product.image} alt={product.name} className="w-full h-full object-cover rounded-md group-hover:scale-105 transition-transform" />
                                                    </div>
                                                    <div className="flex flex-col gap-0.5 min-w-0">
                                                        <h4 className="text-sm font-semibold text-text-primary truncate group-hover:text-primary-brand">{product.name}</h4>
                                                        <span className="text-xs text-text-opacity-60">{product.category}</span>
                                                        <span className="text-xs font-bold text-primary-accent">{product.price}</span>
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-text-opacity-60 py-6 text-center">
                                            No products found matching "<span className="font-semibold text-text-primary">{searchQuery}</span>". Try searching for "Men", "Women", or "Bags".
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile Slide-over Drawer & Backdrop (Smooth Transition) */}
            <div
                className={`fixed inset-0 z-[100] lg:hidden transition-all duration-300 ${menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                    }`}
            >
                {/* Backdrop Overlay */}
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity duration-300"
                    onClick={() => setMenuOpen(false)}
                />

                {/* Drawer Panel (Smooth Right-to-Left Slide) */}
                <aside
                    className={`fixed top-0 right-0 w-[280px] sm:w-[340px] h-full bg-white shadow-2xl flex flex-col justify-between p-5 sm:p-6 overflow-y-auto z-[101] transition-transform duration-300 ease-in-out ${menuOpen ? 'translate-x-0' : 'translate-x-full'
                        }`}
                >
                    <div className="flex flex-col gap-6">
                        {/* Drawer Header with Close Button */}
                        <div className="flex items-center justify-between border-b border-border-light pb-4">
                            <Logo
                                src="/images/img_header_logo.png"
                                alt="NEHDO Wellness"
                                width={140}
                                height={48}
                                className="w-[110px] sm:w-[130px]"
                            />
                            <button
                                onClick={() => setMenuOpen(false)}
                                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-background-tertiary flex items-center justify-center text-text-primary hover:bg-primary-brand hover:text-white transition-colors cursor-pointer"
                                aria-label="Close menu"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Mobile Navigation Links */}
                        <nav className="flex flex-col gap-3.5 sm:gap-4 text-base sm:text-lg">
                            <div onClick={() => setMenuOpen(false)}>
                                <HeaderMenuItem href="/" text="Home" />
                            </div>
                            <div onClick={() => setMenuOpen(false)}>
                                <HeaderMenuItem href="/shop" text="Shop" />
                            </div>
                            <div onClick={() => setMenuOpen(false)}>
                                <HeaderMenuItem href="/categories" text="Categories" />
                            </div>
                            <div onClick={() => setMenuOpen(false)}>
                                <HeaderMenuItem href="/new-arrivals" text="New Arrivals" />
                            </div>
                            <div onClick={() => setMenuOpen(false)}>
                                <HeaderMenuItem href="/about" text="About" />
                            </div>
                            <div onClick={() => setMenuOpen(false)}>
                                <HeaderMenuItem href="/contact" text="Contact" />
                            </div>
                        </nav>
                    </div>

                    {/* Mobile Action Icons Footer */}
                    <div className="border-t border-border-light pt-5 flex items-center justify-around">
                        <button
                            onClick={() => { setMenuOpen(false); setSearchOpen(true); }}
                            aria-label="Search"
                            className="p-1 cursor-pointer"
                        >
                            <img src="/images/img_frame.svg" alt="Search" className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>
                        <Link href="/wishlist" ariaLabel="Wishlist" onClick={() => setMenuOpen(false)}>
                            <img src="/images/img_vector.svg" alt="Wishlist" className="w-4.5 h-4.5 sm:w-5 sm:h-5" />
                        </Link>
                        <Link href="/account" ariaLabel="Account" onClick={() => setMenuOpen(false)}>
                            <img src="/images/img_frame_black_900.svg" alt="Account" className="w-5 h-5 sm:w-6 sm:h-6" />
                        </Link>
                        <Link href="/cart" ariaLabel="Shopping Cart" onClick={() => setMenuOpen(false)}>
                            <img src="/images/img_frame_black_900_30x30.svg" alt="Cart" className="w-5 h-5 sm:w-6 sm:h-6" />
                        </Link>
                    </div>
                </aside>
            </div>
        </header>
    )
}

export default Header

